// components/LandingPane.tsx — marketing strip on the Login screen.
//
// Sits to the left of the auth state machine on desktop (≥860px), or above
// it on mobile. Shown only on the `email` step — once the user begins
// authenticating, the surrounding Login.tsx hides this pane and recentres
// the auth card. The marketing copy is for unconverted visitors, not
// mid-flow signers-in.
//
// Themed by the ScenarioContext: the title, tagline, body bullets, and
// fine-print all switch when the user previews/selects a different
// scenario via the pill row. Phase B: marketing copy is read from
// `scenarios.json` namespace via `t()`, keyed by the effective scenario's
// id (`effective.id`).

import type { CSSProperties } from "react";
import { useTranslation } from "react-i18next";
import { styles } from "../styles";
import { useScenario } from "../scenarios/ScenarioContext";
import ScenarioDropdown from "./ScenarioDropdown";

interface LandingPaneProps {
  onLearnMore: () => void;
  /** When true, render the stacked (mobile) variant. */
  stacked?: boolean;
  /** Optional id used by the Learn-more button's aria-controls. */
  overlayId?: string;
}

interface HeroBullet {
  title: string;
  body: string;
}

export default function LandingPane({
  onLearnMore,
  stacked = false,
  overlayId,
}: LandingPaneProps) {
  const { effective } = useScenario();
  const { t } = useTranslation(["scenarios", "auth"]);
  const paneStyle: CSSProperties = stacked
    ? styles.landingPaneStacked
    : styles.landingPane;

  // The scenarios bundle keys hero bullets as a JSON array; pull it via
  // `returnObjects` and fall back to an empty list if the bundle hasn't
  // loaded for this language yet (the fallbackLng should prevent that,
  // but a defensive default keeps the pane from crashing).
  const heroBullets = t(`scenarios:${effective.id}.heroBullets`, {
    returnObjects: true,
    defaultValue: [] as HeroBullet[],
  }) as HeroBullet[];
  const heroSubtitle = t(`scenarios:${effective.id}.heroSubtitle`);
  const heroFinePrint = t(`scenarios:${effective.id}.heroFinePrint`);
  const pillLabel = t(`scenarios:${effective.id}.pillLabel`, {
    defaultValue: effective.pillLabel,
  });

  return (
    <section style={paneStyle} aria-label={t("auth:landing.aboutPaneLabel", { name: pillLabel })}>
      {/*
        Brand-level positioning, cross-scenario. Sits ABOVE the scenario
        dropdown so the visitor's first read is the product-level promise
        ("a library of novels that haven't been written yet") before their
        eye drops to picking a specific world.
      */}
      <h1 style={styles.landingBrandTagline}>{t("auth:landing.brandTagline")}</h1>
      <p style={styles.landingBrandTaglineSub}>{t("auth:landing.brandTaglineSub")}</p>

      <ScenarioDropdown variant="hero" />
      <p style={styles.landingHeroTagline}>{heroSubtitle}</p>
      {/*
        Aspirational, cross-scenario delivery line — the platform's promise
        to the visitor (the AI-driven dynamic-world bit) rendered once in
        the brand voice, not scenario flavour. Sits above the divider so
        the visitor reads scenario-hero → product-promise → bullets.
      */}
      <p style={styles.landingPlatformPromise}>
        {t("auth:landing.platformPromise")}
      </p>

      <div style={styles.landingDivider} aria-hidden />

      <ul style={styles.landingBulletList}>
        {heroBullets.map((b) => (
          <li key={b.title} style={styles.landingBullet}>
            <span style={styles.landingBulletGlyph} aria-hidden>
              ✦
            </span>
            <span style={styles.landingBulletText}>
              <span style={styles.landingBulletTitle}>{b.title}</span>
              {b.body}
            </span>
          </li>
        ))}
      </ul>

      <div style={styles.landingLearnMoreRow}>
        <button
          type="button"
          style={styles.landingLearnMoreBtn}
          onClick={onLearnMore}
          aria-haspopup="dialog"
          aria-controls={overlayId}
        >
          {t("auth:landing.aboutLink", { name: pillLabel })}
        </button>
      </div>

      {/*
        Trust-signal strip — three quiet value-props lines for paid-ad
        traffic. Smaller and softer than the hero bullets so it reads as
        a TripAdvisor-footer style band rather than a headline. Renders
        in both two-column and stacked variants.
      */}
      <div style={styles.landingTrustStrip}>
        <div style={styles.landingTrustLine}>
          <span style={styles.landingTrustGlyph} aria-hidden>
            ✦
          </span>
          <span>{t("auth:landing.trustSignals.memory")}</span>
        </div>
        <div style={styles.landingTrustLine}>
          <span style={styles.landingTrustGlyph} aria-hidden>
            ✦
          </span>
          <span>{t("auth:landing.trustSignals.noPaywall")}</span>
        </div>
        <div style={styles.landingTrustLine}>
          <span style={styles.landingTrustGlyph} aria-hidden>
            ✦
          </span>
          <span>{t("auth:landing.trustSignals.tasteful")}</span>
        </div>
      </div>

      <div style={styles.landingFinePrint}>{heroFinePrint}</div>
    </section>
  );
}
